import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  getNotificationConfig,
  UserNotification,
} from '../../models/userNotification';
import { FileDownloadService } from '../../../../../../services/file-download.service';
import { UserContextService } from '../../../../../../services/user-context.service';
import { parseBackendUtcTimestamp } from '../../../../../../shared/utils/date-time.utils';

@Component({
  selector: 'app-notification',
  imports: [
    CommonModule,
    DatePipe,
    MatCheckboxModule,
    MatIconModule,
    MatProgressBarModule,
  ],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationComponent {
  readonly notification = input.required<UserNotification>();
  readonly isSelected = input(false);
  readonly showCheckbox = input(true);

  private readonly fileDownload = inject(FileDownloadService);
  private readonly userContext = inject(UserContextService);

  readonly selectionChange = output<string>();

  readonly config = computed(() => getNotificationConfig(this.notification()));

  readonly notificationText = computed(() => {
    const text = this.config().text;
    if (typeof text === 'function') {
      return text(this.notification().content.details);
    }
    return text;
  });

  readonly showProgress = computed(() => this.config().showProgress ?? false);
  readonly showProgressText = computed(
    () => this.config().showProgressText ?? true
  );

  readonly notificationDate = computed(() =>
    parseBackendUtcTimestamp(this.notification().timestamp)
  );

  readonly percentage = computed(
    () => this.notification().content.details.percentage ?? 0
  );

  readonly totalCount = computed(() => {
    const value = this.notification().content.details.total_count;
    return typeof value === 'number' ? value : null;
  });

  readonly processedCount = computed(() => {
    const details = this.notification().content.details;
    const processed = details.processed_count;
    if (typeof processed === 'number') return processed;

    const total = this.totalCount();
    const percentage = details.percentage;
    if (typeof total === 'number' && typeof percentage === 'number') {
      return Math.round((total * percentage) / 100);
    }

    return null;
  });

  readonly summaryLines = computed(() => {
    const buildSummary = this.config().summaryLines;
    if (!buildSummary) return [];
    return buildSummary(this.notification().content.details);
  });

  readonly action = computed(() => this.config().action ?? null);

  readonly showAction = computed(() => {
    const action = this.action();
    if (!action) return false;
    if (action.downloadUrlField) {
      return !!this.notification().content.details[action.downloadUrlField];
    }
    return true;
  });

  readonly iconClass = computed(
    () => `notification-icon--${this.notification().type.toLowerCase()}`
  );

  readonly progressText = computed(() => {
    if (!this.showProgressText()) {
      return null;
    }

    const processed = this.processedCount();
    const total = this.totalCount();
    if (processed === null || total === null) return null;
    return `${processed} de ${total}`;
  });

  onSelectionChange(): void {
    this.selectionChange.emit(this.notification().id);
  }

  onAction(): void {
    const action = this.action();
    if (!action?.downloadUrlField) return;

    const url = this.notification().content.details[
      action.downloadUrlField
    ] as string;
    if (!url) return;

    const companyName = this.userContext.companyName() || 'CCRR';
    const fileName = action.buildFileName
      ? action.buildFileName(this.notification(), companyName)
      : (url.split('/').pop() ?? 'download');

    this.fileDownload.download(url, fileName);
  }
}
